import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CompetitionsService } from '../../core/competitions/competitions';

type JoinState = 'joining' | 'error' | 'need-code';

@Component({
  selector: 'app-join',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './join.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './join.css',
})
export class Join implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly competitions = inject(CompetitionsService);

  readonly state = signal<JoinState>('joining');
  readonly errorMessage = signal<string | null>(null);

  // A FormGroup (not a bare FormControl) so the template's [formGroup]
  // binding gives the <form> an NgSubmit-emitting directive to listen to —
  // a bare formControl with no formGroup on the <form> silently drops
  // (ngSubmit), a real bug caught by live testing, not just unit tests.
  readonly form = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  // '/join' and '/join/:code' are two distinct route entries pointing at this
  // same component, so navigating between them destroys/recreates it —
  // a plain snapshot read is safe, no need to subscribe to paramMap changes.
  ngOnInit(): void {
    const code = this.route.snapshot.paramMap.get('code');
    if (!code) {
      this.state.set('need-code');
      return;
    }
    void this.join(code);
  }

  async submitCode(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    await this.router.navigateByUrl(`/join/${this.form.getRawValue().code.trim()}`);
  }

  private async join(code: string): Promise<void> {
    this.state.set('joining');
    this.errorMessage.set(null);
    try {
      const result = await this.competitions.join(code);
      await this.router.navigateByUrl(`/competitions/${result.competitionId}`);
    } catch (err) {
      this.state.set('error');
      this.errorMessage.set(this.messageFor(err));
    }
  }

  private messageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 404) {
        return "That invite code doesn't match any competition — double-check it and try again.";
      }
      if (err.status === 403) {
        return 'This competition is on hold and not accepting new contestants right now.';
      }
      if (err.status === 409) {
        return "You've already joined this competition, or it's already full.";
      }
    }
    return 'Something went wrong on our end — give it another try in a moment.';
  }
}
